import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { countryName } from '@libs/dtos/merit/countries.catalog';

/**
 * Display-only localization for Kairos Merit catalog values.
 *
 * Catalog values (positions, availability, visa status, languages…) are stored
 * in English — the canonical value the scoring engine keys off. This service
 * maps them to Spanish labels for display in the `es` locale; other locales keep
 * the English value. Translations live in i18n (`merit.options`,
 * `merit.languages`, `merit.levelLabels`).
 */
@Injectable({ providedIn: 'root' })
export class MeritI18nService {
	private readonly transloco = inject(TranslocoService);

	private translateOr(key: string, fallback: string): string {
		// Transloco returns the key itself when the translation is missing.
		const t = this.transloco.translate(key);
		return t && t !== key ? t : fallback;
	}

	/** Localized label for a catalog value (Spanish only; else the English value). */
	option(value: string | null | undefined): string {
		if (!value) return '';
		if (this.transloco.getActiveLang() !== 'es') return value;
		const direct = this.transloco.translate(`merit.options.${value}`);
		if (direct && direct !== `merit.options.${value}`) return direct;
		// Los sueldos se guardan con coma de millares («5,500», «3,000 – 6,000»).
		// En español esa coma se lee como decimal: 5,500 parecería 5,5.
		if (/^(?:\+)?\d{1,3}(?:,\d{3})+(?: \u2013 (?:\+)?\d{1,3}(?:,\d{3})+)?$/.test(value)) {
			return value.replace(/,/g, '.');
		}
		// GCC locations are "Country — City": translate each part (129 values,
		// so per-value entries would be unmaintainable). Cities without an
		// exonym in `merit.cities` stay as-is.
		const parts = value.split(' — ');
		if (parts.length === 2) {
			const country = this.translateOr(`merit.countries.${parts[0]}`, parts[0]);
			if (country !== parts[0]) {
				return `${country} — ${this.translateOr(`merit.cities.${parts[1]}`, parts[1])}`;
			}
		}
		return value;
	}

	/**
	 * The up to three target positions, main one first, as «A · B · C» (07/10:
	 * lists showed only the main one). Falls back to the main one alone.
	 */
	positions(main: string | null | undefined, all?: readonly string[] | null): string {
		const values = [...new Set([main, ...(all ?? [])].filter((value): value is string => !!value))];
		return values.length ? values.map((value) => this.option(value)).join(' · ') : '—';
	}

	/** {label,value} pairs for a catalog, in catalog order. */
	options(values: readonly string[]): { label: string; value: string }[] {
		return values.map((v) => ({ label: this.option(v), value: v }));
	}

	/**
	 * The catalog values whose TRANSLATED label contains the typed text (15/09).
	 * The database stores the English value («F&B Supervisor»), the screen shows
	 * «Supervisor de A&B», and the search box only reaches the database: typing
	 * what one reads found nothing. The screen resolves it and sends the values
	 * next to the text; the value itself is left to the database search.
	 */
	matchingValues(term: string | null | undefined, values: readonly string[]): string[] {
		const needle = (term ?? '').trim().toLowerCase();
		if (needle.length < 2) return [];
		return values.filter((v) => {
			const label = this.option(v).toLowerCase();
			return label !== v.toLowerCase() && label.includes(needle);
		});
	}

	/**
	 * Appends values already stored on the record but missing from the catalog.
	 *
	 * Leads store the catalog *label*, so a value that was deactivated or renamed
	 * in /catalogs after the lead was created no longer comes back from the API.
	 * Without this, opening that lead for edit would silently blank the field and
	 * saving would wipe it — so the stored value stays as a selectable option.
	 */
	withStored(values: readonly string[], ...stored: (string | readonly string[] | null | undefined)[]): string[] {
		const merged = [...values];
		for (const entry of stored) {
			for (const value of Array.isArray(entry) ? entry : [entry as string | null | undefined]) {
				if (value && !merged.includes(value)) merged.push(value);
			}
		}
		return merged;
	}

	/**
	 * Nombre del país de un código ISO alfa-2, en el idioma activo. Lo resuelve
	 * `Intl`, así que no hay 200 claves de i18n que mantener: al cambiar de idioma
	 * la lista se traduce sola.
	 */
	country(code: string | null | undefined): string {
		if (!code) return '';
		return countryName(code, this.transloco.getActiveLang());
	}

	/**
	 * La nacionalidad como se pinta: **el código manda**, y el texto libre solo se
	 * usa cuando no hay código.
	 *
	 * Las fichas anteriores a la cascada solo tienen el texto, y viene en tres
	 * formatos a la vez (países en español, gentilicios en inglés y minúsculas
	 * sueltas). Ese es justo el motivo de que exista el código: en cuanto lo hay,
	 * el texto deja de decidir lo que se ve.
	 */
	nationality(countryCode: string | null | undefined, nationality: string | null | undefined): string {
		return countryCode ? this.country(countryCode) : this.option(nationality);
	}

	/** Localized language name (uses `merit.languages`). */
	language(value: string | null | undefined): string {
		if (!value) return '';
		if (this.transloco.getActiveLang() !== 'es') return value;
		return this.translateOr(`merit.languages.${value}`, value);
	}

	/** Language {label,value} pairs, sorted by the display label with "Other" last. */
	languageOptions(values: readonly string[]): { label: string; value: string }[] {
		const opts = values.map((v) => ({ label: this.language(v), value: v }));
		const other = opts.filter((o) => o.value === 'Other');
		const rest = opts.filter((o) => o.value !== 'Other').sort((a, b) => a.label.localeCompare(b.label, 'es'));
		return [...rest, ...other];
	}

	/** Localized CEFR level label (uses `merit.levelLabels`, keyed by code). */
	level(code: string | null | undefined): string {
		if (!code) return '';
		return this.translateOr(`merit.levelLabels.${code}`, code);
	}

	/** Level {label,value} pairs (value = CEFR code). */
	levelOptions(codes: readonly string[]): { label: string; value: string }[] {
		return codes.map((c) => ({ label: this.level(c), value: c }));
	}
}
